import { DateTime } from "luxon";

import parse from "@/utils/parse";
import * as k4util from "@/utils/k4util";
import extendWithDescriptor from "@/utils/extend";
import { ordinalSuffixOf } from "@/utils/ordinal";

import Node from "./baseNode";
import * as path from "../path";

export const DEFAULT_OPERATORS_MAP = {
	"==": "is",
	"!=": "is not",
	">": "greater than",
	">=": "equal to or greater than",
	"<": "less than",
	"<=": "equal to or less than",
};

export const DATETIME_OPERATORS_MAP = {
	"==": "is",
	"!=": "is not",
	">": "is after",
	">=": "is or after",
	"<": "is before",
	"<=": "is or before",
};

export default class ConditionNode extends Node {
	static get modelName() {
		return "ConditionNode";
	}

	static create(k4node) {
		let node = new ConditionNode(k4node);

		// Mixins based on type
		const targetPath = k4node.child("properties/path").value();
		const target = k4node.root().child(targetPath);
		if (target) {
			const isDate = k4util.isTypeDate(target);
			const isTime = k4util.isTypeTime(target);

			// Extend with methods specific to date/time
			if (isDate) node = extendWithDescriptor(node, DateValue);
			if (isTime) node = extendWithDescriptor(node, TimeValue);

			// Extend with date/time readable operator
			if (isDate || isTime) node = extendWithDescriptor(node, DateTimeOperator);
		}

		return node;
	}

	constructor(k4node) {
		super(k4node);

		this.targetPath = path.normalize(k4node.child("properties/path").value());
		if (k4node.root().child(this.targetPath)) {
			this.target = path.$ref(this.targetPath);
		} else {
			// Ignore invalid paths to prevent JSON pointer errors
			this.target = null;
		}
		this.operator = k4node.child("properties/operator").value();
		this.value = k4node.child("properties/value").value();
	}

	get targetIsTime() {
		return this.target && this.target.isTime;
	}

	get targetIsDate() {
		return this.target && this.target.isDate;
	}

	get targetIsVariable() {
		return this.target && this.target.isVariable || this.targetPath.includes("/variables/");
	}

	get targetIsEvent() {
		return this.target && this.target.isEvent || this.targetPath.includes("/events/");
	}

	// Returns human-readable value
	get readableValue() {
		return this.value;
	}

	// Returns all mappings of operator to human-readable string
	get operatorsMap() {
		return DEFAULT_OPERATORS_MAP;
	}

	// Returns human-readable value for condition operator
	// Ex. For a number condition with a "<=" operator it should return 'Is greater than or equal to'
	get readableOperator() {
		return this.operatorsMap[this.operator];
	}
}

const DateValue = {
	get readableValue() {
		switch (this.target.type) {
			case "date":
				// Format dates
				const date = parse.dateTime(this.value);
				if (date) return date.toLocaleString(DateTime.DATE_FULL);
				break;
			case "day":
				// Format 'day's
				// (ex. 2 -> '2nd', 3 -> '3rd', etc.)
				const val = this.value;
				return ordinalSuffixOf(val);
			default:
				return this.value;
		}
	},
};

const TimeValue = {
	get readableValue() {
		if (this.target.type === "time") {
			const time = parse.time(this.value);
			if (time) return time.toLocaleString(DateTime.TIME_SIMPLE);
		}
		return this.value;
	},
};

const DateTimeOperator = {
	get operatorsMap() {
		return DATETIME_OPERATORS_MAP;
	},

	get readableOperator() {
		const suffix = this.target.type === "day" ? " the" : "";
		return this.operatorsMap[this.operator] + suffix;
	},
};
